// ===================================================================
// Time & Labor — Reports
// Export-ready labor reports with date range filter, no disaster requirement
// ===================================================================

import { useEffect, useMemo, useState } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Group, Select, Button,
  LoadingOverlay, SegmentedControl, TextInput,
} from '../../components/gab/GabComponents';
import { IconDownload, IconReportAnalytics, IconCalendar } from '@tabler/icons-react';
import { useTimeLaborStore } from '../../store/timeLaborStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { formatCurrency, stripCurrency } from '../../lib/formatUtils';
import { exportToCSV } from '../../lib/exportUtils';

const TEAL = '#0D9488';

/** Extract the task label from activityDescription (e.g. "Debris Removal: cleaned lot" -> "Debris Removal") */
function extractTaskLabel(desc: string): string {
  if (!desc) return '';
  const colonIdx = desc.indexOf(':');
  return colonIdx > 0 ? desc.substring(0, colonIdx).trim() : desc;
}

function pf(v: string | number | undefined): number {
  if (typeof v === 'number') return v || 0;
  return parseFloat(stripCurrency(v || '')) || 0;
}

type ReportType = 'detail' | 'employee_summary' | 'project_summary';

export default function Reports() {
  useModulePermission('timelabor');
  const { fetchEmployees, laborRecords, loadingRecords, fetchLaborRecords } = useTimeLaborStore();
  const { projects, fetchProjects } = usePAGrantsStore();
  const activeIncident = useAuthStore(s => s.activeIncident);

  const [reportType, setReportType] = useState<ReportType>('detail');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterProject, setFilterProject] = useState<string | null>(null);
  const [filterEmployee, setFilterEmployee] = useState<string | null>(null);

  useEffect(() => {
    fetchEmployees();
    fetchLaborRecords();
    fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Project options (include "Unassigned")
  const projectOptions = useMemo(() => [
    { value: '__unassigned__', label: 'Unassigned' },
    ...projects.map(p => ({
      value: String(p.Rid),
      label: `${p.projectNumber ? p.projectNumber + ' - ' : ''}${p.projectTitle || 'Untitled'}`,
    })),
  ], [projects]);

  // Employee filter options
  const employeeOptions = useMemo(() => {
    const names = new Set(laborRecords.map(r => r.employeeName).filter(Boolean));
    return [...names].sort().map(n => ({ value: n, label: n }));
  }, [laborRecords]);

  const projectLookup = useMemo(() => {
    const m = new Map<string, string>();
    projects.forEach(p => m.set(String(p.Rid), p.projectTitle || p.projectNumber || `#${p.Rid}`));
    return m;
  }, [projects]);

  // Filter by active incident + user filters
  const filtered = useMemo(() => {
    let list = laborRecords;
    if (activeIncident) {
      const incId = String(activeIncident.id);
      list = list.filter(r => String(r.incidentId ?? '') === incId);
    }
    if (filterProject === '__unassigned__') {
      list = list.filter(r => !r.projectId);
    } else if (filterProject) {
      list = list.filter(r => String(r.projectId) === filterProject);
    }
    if (filterEmployee) list = list.filter(r => r.employeeName === filterEmployee);
    if (dateFrom) list = list.filter(r => r.workDate >= dateFrom);
    if (dateTo) list = list.filter(r => r.workDate <= dateTo);
    return list;
  }, [laborRecords, activeIncident, filterProject, filterEmployee, dateFrom, dateTo]);

  const handleExport = () => {
    if (reportType === 'detail') {
      exportToCSV('labor-detail-report', [
        'Date', 'Employee Name', 'Employee ID', 'Title', 'Project',
        'Activity', 'FEMA Category',
        'Regular Hours', 'OT Hours', 'Regular Rate', 'OT Rate', 'Fringe %',
        'Labor Cost', 'Fringe Cost', 'Total Cost', 'Activity Description', 'Site/Notes',
      ], filtered.sort((a, b) => a.workDate.localeCompare(b.workDate)).map(r => [
        r.workDate, r.employeeName, r.employeeId, r.employeeTitle,
        r.projectId ? (projectLookup.get(String(r.projectId)) || String(r.projectId)) : 'Unassigned',
        extractTaskLabel(r.activityDescription), r.category,
        r.hoursRegular, r.hoursOvertime, r.rateRegular, r.rateOvertime,
        r.fringeBenefitsPct, r.laborCost, r.fringeCost, r.totalCost,
        r.activityDescription, r.notes,
      ]));
    } else if (reportType === 'employee_summary') {
      const map = new Map<string, { name: string; id: string; regHrs: number; otHrs: number; labor: number; fringe: number; total: number; days: Set<string> }>();
      filtered.forEach(r => {
        if (!map.has(r.employeeName)) map.set(r.employeeName, { name: r.employeeName, id: r.employeeId, regHrs: 0, otHrs: 0, labor: 0, fringe: 0, total: 0, days: new Set() });
        const row = map.get(r.employeeName)!;
        row.regHrs += pf(r.hoursRegular);
        row.otHrs += pf(r.hoursOvertime);
        row.labor += pf(r.laborCost);
        row.fringe += pf(r.fringeCost);
        row.total += pf(r.totalCost);
        row.days.add(r.workDate);
      });
      const rows = [...map.values()].sort((a, b) => b.total - a.total);
      exportToCSV('labor-employee-summary', [
        'Employee', 'Employee ID', 'Days Worked', 'Regular Hours', 'OT Hours',
        'Total Hours', 'Labor Cost', 'Fringe Cost', 'Total Cost',
      ], rows.map(r => [
        r.name, r.id, String(r.days.size), r.regHrs.toFixed(1), r.otHrs.toFixed(1),
        (r.regHrs + r.otHrs).toFixed(1), r.labor.toFixed(2), r.fringe.toFixed(2), r.total.toFixed(2),
      ]));
    } else {
      const map = new Map<string, { project: string; regHrs: number; otHrs: number; labor: number; fringe: number; total: number; employees: Set<string> }>();
      filtered.forEach(r => {
        const pKey = r.projectId ? String(r.projectId) : '__unassigned__';
        const pName = r.projectId ? (projectLookup.get(String(r.projectId)) || String(r.projectId)) : 'Unassigned';
        if (!map.has(pKey)) map.set(pKey, { project: pName, regHrs: 0, otHrs: 0, labor: 0, fringe: 0, total: 0, employees: new Set() });
        const row = map.get(pKey)!;
        row.regHrs += pf(r.hoursRegular);
        row.otHrs += pf(r.hoursOvertime);
        row.labor += pf(r.laborCost);
        row.fringe += pf(r.fringeCost);
        row.total += pf(r.totalCost);
        row.employees.add(r.employeeName);
      });
      const rows = [...map.values()].sort((a, b) => b.total - a.total);
      exportToCSV('labor-project-summary', [
        'Project', 'Employees', 'Regular Hours', 'OT Hours',
        'Total Hours', 'Labor Cost', 'Fringe Cost', 'Total Cost',
      ], rows.map(r => [
        r.project, String(r.employees.size), r.regHrs.toFixed(1), r.otHrs.toFixed(1),
        (r.regHrs + r.otHrs).toFixed(1), r.labor.toFixed(2), r.fringe.toFixed(2), r.total.toFixed(2),
      ]));
    }
  };

  const totalHrs = filtered.reduce((s, r) => s + pf(r.hoursRegular) + pf(r.hoursOvertime), 0);
  const totalCost = filtered.reduce((s, r) => s + pf(r.totalCost), 0);

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={loadingRecords && laborRecords.length === 0} />

      <Group justify="space-between" mb="md">
        <div>
          <Title order={3}>Labor Reports</Title>
          <Text size="sm" c="dimmed">Generate and export force account labor reports</Text>
        </div>
      </Group>

      {/* Stats */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} mb="lg">
        <Paper p="sm" withBorder ta="center" style={{ borderTop: `3px solid ${TEAL}` }}>
          <Text size="xl" fw={700}>{filtered.length}</Text>
          <Text size="xs" c="dimmed">Records in Range</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #2563EB' }}>
          <Text size="xl" fw={700}>{totalHrs.toFixed(1)}</Text>
          <Text size="xs" c="dimmed">Total Hours</Text>
        </Paper>
        <Paper p="sm" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
          <Text size="xl" fw={700}>{formatCurrency(totalCost.toFixed(2))}</Text>
          <Text size="xs" c="dimmed">Total Cost</Text>
        </Paper>
      </SimpleGrid>

      {/* Controls */}
      <Paper p="md" withBorder mb="lg">
        <Text fw={600} mb="sm">Report Options</Text>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 5 }} spacing="sm" mb="md">
          <div>
            <Text size="xs" mb={4}>Report Type</Text>
            <SegmentedControl size="xs" fullWidth value={reportType} onChange={v => setReportType(v as ReportType)} data={[
              { value: 'detail', label: 'Detail' },
              { value: 'employee_summary', label: 'By Employee' },
              { value: 'project_summary', label: 'By Project' },
            ]} />
          </div>
          <TextInput size="xs" label="Date From" type="date" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)} leftSection={<IconCalendar size={14} />} />
          <TextInput size="xs" label="Date To" type="date" value={dateTo}
            onChange={e => setDateTo(e.target.value)} leftSection={<IconCalendar size={14} />} />
          <Select size="xs" label="Employee" data={employeeOptions} value={filterEmployee}
            onChange={setFilterEmployee} clearable searchable placeholder="All Employees" />
          <Select size="xs" label="Project" data={projectOptions} value={filterProject}
            onChange={setFilterProject} clearable searchable placeholder="All Projects" />
        </SimpleGrid>
        <Button color={TEAL} leftSection={<IconDownload size={14} />} onClick={handleExport}>
          Export {reportType === 'detail' ? 'Detail' : reportType === 'employee_summary' ? 'Employee Summary' : 'Project Summary'} CSV
        </Button>
      </Paper>

      <Paper p="md" withBorder bg="gray.0">
        <Group gap={8}>
          <IconReportAnalytics size={18} color={TEAL} />
          <Text size="sm">
            Select a report type and date range above, then click Export to download.
            The <strong>Detail</strong> report includes every time entry record.
            <strong> By Employee</strong> aggregates hours and costs per employee.
            <strong> By Project</strong> aggregates per project with employee counts (includes "Unassigned" group).
          </Text>
        </Group>
      </Paper>
    </div>
  );
}
